"use client"

// src/context/CategoryContext.tsx

import { createContext, useContext, useState, useCallback, type ReactNode } from "react"
import { toast } from "react-hot-toast"
import { AxiosError } from "axios"
import {
  createCategory as createCategoryService,
  getCategories as getCategoriesService,
  updateCategory as updateCategoryService,
  deleteCategory as deleteCategoryService,
} from "../services/auth"
import { useAuth } from "./AuthContext"
import { slugify } from "@/utils/slugify"

// Дефиниция на типа за категория
type Category = {
  id: number
  name: string
  parentId: number | null
  parent?: Category | null
  children: Category[]
  slug?: string // Add slug property
  image?: string
}

// Интерфейс за контекста на категориите
interface CategoryContextType {
  categories: Category[]
  fetchCategories: () => Promise<void>
  createCategory: (categoryName: string, parentCategoryId?: number) => Promise<void>
  updateCategory: (id: number, categoryName: string, parentCategoryId?: number) => Promise<void>
  deleteCategory: (id: number) => Promise<void>
  getCategoryUrl: (categoryName: string, basePath?: string) => string
  getCategoryBySlug: (slug: string) => Category | undefined
}

// Създаване на контекста за категориите
const CategoryContext = createContext<CategoryContextType | undefined>(undefined)

// Провайдър на категориите, който обгръща част от приложението
export const CategoryProvider = ({ children }: { children: ReactNode }) => {
  const categoryContext = useProvideCategory()
  return <CategoryContext.Provider value={categoryContext}>{children}</CategoryContext.Provider>
}

// Hook за използване на контекста за категории в компоненти
export const useCategory = (): CategoryContextType => {
  const context = useContext(CategoryContext)
  if (!context) {
    throw new Error("useCategory must be used within a CategoryProvider")
  }
  return context
}

// Основната логика на контекста за категории
const useProvideCategory = (): CategoryContextType => {
  const [categories, setCategories] = useState<Category[]>([])

  // Функция за обработка на грешки
  const handleError = useCallback((error: unknown, defaultMessage: string) => {
    let message = defaultMessage

    if (error instanceof AxiosError) {
      message = error.response?.data?.message || defaultMessage
    } else if (error instanceof Error) {
      message = error.message
    }

    console.error("Category Error:", message)
    toast.error(message, {
      style: {
        border: "1px solid #713200",
        padding: "16px",
        color: "#713200",
      },
      iconTheme: {
        primary: "#713200",
        secondary: "#FFFAEE",
      },
    })
  }, [])

  // Generate slug for each category
  const processCategories = useCallback((cats: Category[]): Category[] => {
    return cats.map((cat) => ({
      ...cat,
      slug: slugify(cat.name),
    }))
  }, [])

  // Извличане на категориите от сървъра
  const fetchCategories = useCallback(async () => {
    try {
      const response = await getCategoriesService()
      const processedCategories = processCategories(response || [])
      setCategories(processedCategories)
    } catch (error: unknown) {
      handleError(error, "Неуспешно зареждане на категориите.")
    }
  }, [handleError, , processCategories])

  // Get category URL by name
  const getCategoryUrl = useCallback((categoryName: string, basePath = "/kucheta/hrana") => {
    const slug = slugify(categoryName)
    return `${basePath}/${slug}`
  }, [])

  // Get category by slug
  const getCategoryBySlug = useCallback(
    (slug: string) => {
      return categories.find((category) => slugify(category.name) === slug)
    },
    [categories],
  )

  // Създаване на нова категория
  const createCategory = useCallback(
    async (categoryName: string, parentCategoryId?: number) => {
      try {
        await createCategoryService(categoryName, parentCategoryId)
        toast.success("Категорията е създадена успешно.", {
          style: {
            border: "1px solid #713200",
            padding: "16px",
            color: "#713200",
          },
          iconTheme: {
            primary: "#713200",
            secondary: "#FFFAEE",
          },
        })
        await fetchCategories() // Обновяване на категориите
      } catch (error: unknown) {
        handleError(error, "Неуспешно създаване на категория.")
      }
    },
    [handleError, fetchCategories],
  )

  // Актуализиране на съществуваща категория
  const updateCategory = useCallback(
    async (id: number, categoryName: string, parentCategoryId?: number) => {
      try {
        await updateCategoryService(id, categoryName, parentCategoryId)
        toast.success("Категорията е актуализирана успешно.", {
          style: {
            border: "1px solid #713200",
            padding: "16px",
            color: "#713200",
          },
          iconTheme: {
            primary: "#713200",
            secondary: "#FFFAEE",
          },
        })
        await fetchCategories() // Обновяване на категориите
      } catch (error: unknown) {
        handleError(error, "Неуспешно актуализиране на категорията.")
      }
    },
    [handleError, fetchCategories],
  )

  // Изтриване на категория
  const deleteCategory = useCallback(
    async (id: number) => {
      try {
        await deleteCategoryService(id)
        toast.success("Категорията е изтрита успешно.", {
          style: {
            border: "1px solid #713200",
            padding: "16px",
            color: "#713200",
          },
          iconTheme: {
            primary: "#713200",
            secondary: "#FFFAEE",
          },
        })
        await fetchCategories() // Обновяване на категориите
      } catch (error: unknown) {
        handleError(error, "Неуспешно изтриване на категорията.")
      }
    },
    [handleError, fetchCategories],
  )

  return {
    categories,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    getCategoryUrl,
    getCategoryBySlug,
  }
}

